import React from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Divider, Grid
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'
import { AppButtonIcon, AppCardTitle, AppFilterChip, AppRetailerBlock } from '@components'
import { dsColorNew } from "@palette"
import { ALERTS_ICONS } from "Pages/ReportsAlerts/Alerts/@components/Tabs"
import { ALERTS } from "Pages/ReportsAlerts/Alerts/@constants"
import {
	alertsSection,
	isMinMaxPriceTabEmpty, isMinMaxPriceTabLoading,
	isNoAvailabilityTabEmpty, isNoAvailabilityTabLoading,
	isPriceChangeTabEmpty, isPriceChangeTabLoading,
	isPriceComparisonTabEmpty, isPriceComparisonTabLoading,
	isRatingTabEmpty, isRatingTabLoading,
} from "Pages/ReportsAlerts/Alerts/@signals"
import { offices, reports } from "@signals"


const DEMO_NOTIFICATIONS = [
	{
		market: 'Spain',
		condition: { name: 'Spain Cheaper Alert', comparator: 'Cheaper', value: 15, unit: '%' },
		pickupDate: '15 Mar 2025',
		lor: 5,
		category: { main: 'Economy', comp: 'Economy' },
		acriss: { main: 'ECMR', comp: 'ECMR' },
		fuelPolicy: { main: 'Full/Full', comp: 'Full/Full' },
		transmission: { main: 'Manual', comp: 'Manual' },
		mileage: { main: 'Unlimited', comp: 'Unlimited' },
		terminal: { main: 'In terminal', comp: 'In terminal' },
		insurance: { main: 'Basic', comp: 'Basic' },
		carDescription: { main: 'Fiat 500 or similar', comp: 'Toyota Yaris or similar' },
		site: { main: 'rentalspot.demo', comp: 'pricefly.demo' },
		provider: { main: 'AutoRent', comp: 'AutoRent' },
		price: { main: '92.30', comp: '78.45' },
		diff: -15.0,
	},
	{
		market: 'United Kingdom',
		condition: { name: 'UK Price Difference', comparator: 'Cheaper/More expensive', value: 20, unit: '$' },
		pickupDate: '20 Mar 2025',
		lor: 7,
		category: { main: 'Compact', comp: 'Compact' },
		acriss: { main: 'CDMR', comp: 'CDMR' },
		fuelPolicy: { main: 'Full/Full', comp: 'Pick up Full/Return Full' },
		transmission: { main: 'Automatic', comp: 'Automatic' },
		mileage: { main: 'Unlimited', comp: '1500 km' },
		terminal: { main: 'In terminal', comp: 'Shuttle bus' },
		insurance: { main: 'Full', comp: 'Basic' },
		carDescription: { main: 'VW Golf or similar', comp: 'Ford Focus or similar' },
		site: { main: 'eurodrive.demo', comp: 'rentalspot.demo' },
		provider: { main: 'SpeedCar', comp: 'SpeedCar' },
		price: { main: '145.80', comp: '122.60' },
		diff: 18.9,
	},
	{
		market: 'France',
		condition: { name: 'France More Expensive', comparator: 'More expensive', value: 25, unit: '%' },
		pickupDate: '10 Apr 2025',
		lor: 3,
		category: { main: 'SUV', comp: 'SUV' },
		acriss: { main: 'SFMR', comp: 'SFMR' },
		fuelPolicy: { main: 'Full/Full', comp: 'Full/Full' },
		transmission: { main: 'Automatic', comp: 'Manual' },
		mileage: { main: 'Unlimited', comp: 'Unlimited' },
		terminal: { main: 'Shuttle bus', comp: 'In terminal' },
		insurance: { main: 'Basic', comp: 'Basic' },
		carDescription: { main: 'Peugeot 3008 or similar', comp: 'Nissan Qashqai or similar' },
		site: { main: 'pricefly.demo', comp: 'bookride.demo' },
		provider: { main: 'DrivePro', comp: 'DrivePro' },
		price: { main: '210.50', comp: '168.40' },
		diff: 25.0,
	},
	{
		market: 'Germany',
		condition: { name: 'Germany Economy', comparator: 'Cheaper', value: 10, unit: '$' },
		pickupDate: '05 Apr 2025',
		lor: 14,
		category: { main: 'Intermediate', comp: 'Intermediate' },
		acriss: { main: 'IDMR', comp: 'IDMR' },
		fuelPolicy: { main: 'Full/Full', comp: 'Full/Full' },
		transmission: { main: 'Manual', comp: 'Manual' },
		mileage: { main: '2000 km', comp: 'Unlimited' },
		terminal: { main: 'In terminal', comp: 'In terminal' },
		insurance: { main: 'Full', comp: 'Full' },
		carDescription: { main: 'Opel Astra or similar', comp: 'Skoda Octavia or similar' },
		site: { main: 'bookride.demo', comp: 'autolink.demo' },
		provider: { main: 'Velocar', comp: 'Velocar' },
		price: { main: '385.20', comp: '362.10' },
		diff: -6.0,
	},
]

// Same legend and colours as the real Price Comparison notifications (PriceComparison/index.jsx, columns/name.jsx)
const LEGEND = [
	{ icon: 'percent', label: 'Percentage' },
	{ icon: 'money-bills', label: 'Currency', color: dsColorNew.successText },
	{ icon: 'chart-line-down', label: 'Cheaper', color: dsColorNew.dangerRed },
	{ icon: 'chart-line-up', label: 'More Expensive', color: dsColorNew.successText },
	{ icon: 'chart-line-up-down', label: 'Price Difference', color: dsColorNew.noteAmber },
]

const ICON_MAP = {
	'More expensive': { icon: 'chart-line-up', color: dsColorNew.successText },
	'Cheaper': { icon: 'chart-line-down', color: dsColorNew.dangerRed },
	'Cheaper/More expensive': { icon: 'chart-line-up-down', color: dsColorNew.noteAmber },
	'$': { icon: 'money-bills', color: dsColorNew.successText },
	'%': { icon: 'percent', color: dsColorNew.black },
}

// The active tab's [empty, loading] signals: the demo only stands in while that tab has no real notifications
const TAB_STATE = {
	[ALERTS.PRICE_COMPARISON]: [isPriceComparisonTabEmpty, isPriceComparisonTabLoading],
	[ALERTS.NO_AVAILABILITY]: [isNoAvailabilityTabEmpty, isNoAvailabilityTabLoading],
	[ALERTS.PRICE_CHANGE]: [isPriceChangeTabEmpty, isPriceChangeTabLoading],
	[ALERTS.MIN_MAX_PRICE]: [isMinMaxPriceTabEmpty, isMinMaxPriceTabLoading],
	[ALERTS.RATING]: [isRatingTabEmpty, isRatingTabLoading],
}

const textSx = { fontSize: 13, textAlign: 'center' }
const dividerSx = { my: 1, width: '80%', borderColor: dsColorNew.grayBorder }

// Same cell as Pages/ReportsAlerts/Alerts/Notifications/@components/Table/columns/comparator.jsx
function ComparatorCell({ main, comp }) {
	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
		<Typography sx={{ ...textSx, fontWeight: 'bold' }}>{main}</Typography>
		{comp && <>
			<Divider sx={dividerSx}/>
			<Typography sx={textSx}>{comp}</Typography>
		</>}
	</Box>
}

// Same cell as Pages/ReportsAlerts/Alerts/Notifications/@components/Table/columns/site.jsx
function SiteCell({ main, comp, market }) {
	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
		<AppRetailerBlock retailer={main} country={market}/>
		{comp && <>
			<Divider sx={dividerSx}/>
			<AppRetailerBlock retailer={comp} country={market}/>
		</>}
	</Box>
}


export default function NotificationsDemoData() {
	const [isEmpty, isLoading] = TAB_STATE[alertsSection.value] || []
	const hasRealData = !!reports.value && !!offices.value && !isEmpty?.value && !isLoading?.value
	if (hasRealData) return null

	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
						<AppCardTitle
							icon={ALERTS_ICONS[ALERTS.PRICE_COMPARISON]}
							title="Notifications: Price Comparison"
							sx={{ '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}
						/>
						<Box data-tour-id="tour-alert-legend" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
							{LEGEND.map(l => <AppFilterChip key={l.label} icon={l.icon} iconSx={l.color ? { color: l.color } : {}} label={l.label}/>)}
						</Box>
					</Box>

					<Box sx={{ overflowX: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow sx={{ '& th': { whiteSpace: 'nowrap', textAlign: 'center' } }}>
									<TableCell>Condition</TableCell>
									<TableCell>Pickup Date</TableCell>
									<TableCell>LOR</TableCell>
									<TableCell>Category</TableCell>
									<TableCell>Acriss</TableCell>
									<TableCell>Fuel Policy</TableCell>
									<TableCell>Transmission</TableCell>
									<TableCell>Mileage</TableCell>
									<TableCell>Vendor Airport Location</TableCell>
									<TableCell>Insurance</TableCell>
									<TableCell>Car Description</TableCell>
									<TableCell>Site</TableCell>
									<TableCell>Provider</TableCell>
									<TableCell>Price</TableCell>
									<TableCell>Currency</TableCell>
									<TableCell>Diff</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{DEMO_NOTIFICATIONS.map((n, idx) => (
									<TableRow key={idx} sx={{
										backgroundColor: idx % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt,
										'&:hover': { backgroundColor: dsColorNew.hoverBgLight },
										'& td': { py: 1.25, fontSize: 13 },
									}}>
										<TableCell>
											<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
												<Typography sx={{ ...textSx, fontWeight: 'bold' }}>{n.condition.name}</Typography>
												<Divider sx={dividerSx}/>
												<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
													<Typography sx={{ ...textSx, fontWeight: 'bold' }}>{n.condition.value}</Typography>
													<IconSeeker
														icon={ICON_MAP[n.condition.unit]?.icon}
														sx={{ mx: .7, fontSize: 14, color: ICON_MAP[n.condition.unit]?.color }}
													/>
													<IconSeeker
														tooltip={n.condition.comparator}
														icon={ICON_MAP[n.condition.comparator]?.icon}
														sx={{ fontSize: 14, color: ICON_MAP[n.condition.comparator]?.color }}
													/>
												</Box>
											</Box>
										</TableCell>

										<TableCell>
											<Typography sx={{ ...textSx, whiteSpace: 'nowrap' }}>{n.pickupDate}</Typography>
										</TableCell>

										<TableCell>
											<Typography sx={textSx}>{n.lor}</Typography>
										</TableCell>

										<TableCell><ComparatorCell main={n.category.main} comp={n.category.comp}/></TableCell>
										<TableCell><ComparatorCell main={n.acriss.main} comp={n.acriss.comp}/></TableCell>
										<TableCell><ComparatorCell main={n.fuelPolicy.main} comp={n.fuelPolicy.comp}/></TableCell>
										<TableCell><ComparatorCell main={n.transmission.main} comp={n.transmission.comp}/></TableCell>
										<TableCell><ComparatorCell main={n.mileage.main} comp={n.mileage.comp}/></TableCell>
										<TableCell><ComparatorCell main={n.terminal.main} comp={n.terminal.comp}/></TableCell>
										<TableCell><ComparatorCell main={n.insurance.main} comp={n.insurance.comp}/></TableCell>
										<TableCell><ComparatorCell main={n.carDescription.main} comp={n.carDescription.comp}/></TableCell>
										<TableCell><SiteCell main={n.site.main} comp={n.site.comp} market={n.market}/></TableCell>
										<TableCell><ComparatorCell main={n.provider.main} comp={n.provider.comp}/></TableCell>

										<TableCell>
											<ComparatorCell main={n.price.main} comp={n.price.comp}/>
										</TableCell>

										<TableCell><ComparatorCell main="EUR" comp="EUR"/></TableCell>

										{/* Same cell as Pages/ReportsAlerts/Alerts/Notifications/@components/Table/columns/diff.jsx */}
										<TableCell>
											<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
												<Typography sx={textSx}>{n.diff > 0 ? '+' : ''}{n.diff}%</Typography>
												<Divider sx={dividerSx}/>
												<AppButtonIcon
													icon="magnifying-glass-chart"
													tooltip="View in real time searches"
													sx={{ width: 28, height: 28, border: 'none', color: dsColorNew.appBlue, mt: 0.5, fontSize: 28, '&:hover': { border: 'none' } }}
												/>
											</Box>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</CardContent>
			</Card>
		</Grid>
	</Grid>
}
